import { useTranslation } from 'react-i18next';

import { Input } from '@codes/ui-kit/components/ui/input';
import { Label } from '@codes/ui-kit/components/ui/label';

import type { KeyView } from '@codes/codes-keys';

import { maskApiKey } from '../utils';

interface CodesKeyEditFieldsProps {
    formData: KeyView | null;
    hidden: boolean;
    onHiddenChange: (value: boolean) => void;
    invalid: boolean;
    onInvalidChange: (value: boolean) => void;
    isLoading: boolean;
}

export const CodesKeyEditFields = ({
    formData,
    hidden,
    onHiddenChange,
    invalid,
    onInvalidChange,
    isLoading,
}: CodesKeyEditFieldsProps) => {
    const { t } = useTranslation();

    return (
        <>
            <div className="flex flex-col gap-y-2">
                <Label htmlFor="apiKey">{t('codesKeys.form.apiKeyLabel')}</Label>
                <Input
                    id="apiKey"
                    value={maskApiKey(formData?.apiKey || '')}
                    disabled
                    className="bg-muted font-mono"
                    autoComplete="off"
                />
                <p className="text-xs text-sub">{t('codesKeys.form.apiKeyReadOnly')}</p>
            </div>

            <div className="flex flex-col gap-y-2">
                <div className="flex items-center gap-2">
                    <input
                        id="hidden"
                        type="checkbox"
                        checked={hidden}
                        onChange={e => onHiddenChange(e.target.checked)}
                        disabled={isLoading}
                        className="h-4 w-4"
                    />
                    <Label htmlFor="hidden" className="cursor-pointer">
                        {t('codesKeys.form.hiddenLabel')}
                    </Label>
                </div>
                <p className="text-xs text-sub">{t('codesKeys.form.hiddenDescription')}</p>
            </div>

            <div className="flex flex-col gap-y-2">
                <div className="flex items-center gap-2">
                    <input
                        id="invalid"
                        type="checkbox"
                        checked={invalid}
                        onChange={e => onInvalidChange(e.target.checked)}
                        disabled={isLoading}
                        className="h-4 w-4"
                    />
                    <Label htmlFor="invalid" className="cursor-pointer">
                        {t('codesKeys.form.invalidLabel')}
                    </Label>
                </div>
                <p className="text-xs text-sub">{t('codesKeys.form.invalidDescription')}</p>
            </div>

            {(formData?.owner$ || formData?.createdAt) && (
                <div className="space-y-2 rounded-md border p-3 bg-muted/20">
                    {formData?.owner$ && (
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">{t('codesKeys.form.ownerLabel')}</span>
                            <span>{formData.owner$.name || formData.owner$.id || ''}</span>
                        </div>
                    )}

                    {formData?.createdAt && (
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">{t('codesKeys.form.createdLabel')}</span>
                            <span>{new Date(formData.createdAt).toLocaleString()}</span>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};
